import moment from 'moment';
import { FREE_USER_JOB_PITCH_LIMIT } from 'constants/constants';
import { parseBriefSpec } from 'helpers/briefSpec';
import {
  getClientActivityLabel,
  getClientActivityStatus,
} from 'helpers/clientActivityStatus';
import { formatMoney } from 'helpers/HelperFunctions';
import { getJobCardExcerpt } from 'helpers/jobCardExcerpt';
import {
  hasSearchingFlag,
  hasUrgentFlag,
  parseTitleFlags,
} from 'helpers/parseTitleFlags';
import { calendarDaysAgoLabel } from 'helpers/postedAgo';
import { formatWhereLine } from './formatWhereLine';

/** Aggregated / system posters — omit the client row entirely. */
const HIDDEN_CLIENT_NAME = /Twine Jobs|^private$/i;

/**
 * @param {string} name
 * @returns {string}
 */
export function getInitials(name = '') {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return '?';
  }

  if (words.length > 1) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  }

  return words[0].slice(0, 2).toUpperCase();
}

/**
 * Sentence case for role chips: first letter up, rest of the string as stored
 * casing would break acronyms, so only force the leading character.
 *
 * @param {string} role
 * @returns {string}
 */
export function toRoleSentenceCase(role = '') {
  if (!role) {
    return '';
  }

  const trimmed = role.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/**
 * @param {Object} owner
 * @param {boolean} isPrivate
 * @returns {string}
 */
export function getClientLocation(owner, isPrivate) {
  if (!owner || !owner.location) {
    return '';
  }

  const { city, country, text } = owner.location;

  if (country) {
    return city && !isPrivate ? `${city}, ${country}` : country;
  }

  if (text && text.toLowerCase() !== 'private' && !isPrivate) {
    return text;
  }

  return '';
}

/**
 * @param {string} externalUrl
 * @returns {string}
 */
export function getExternalHostname(externalUrl) {
  if (!externalUrl) {
    return '';
  }

  try {
    return new URL(externalUrl).hostname.replace(/^www\./i, '');
  } catch (e) {
    return '';
  }
}

/**
 * Budget chip text matching the job page (never "Negotiable").
 *
 * @param {Object} brief
 * @param {string|null} parsedBudget
 * @returns {string}
 */
export function getBudgetChipLabel(brief, parsedBudget) {
  const hasBudget = brief.amount && brief.amount !== 0 && brief.amount !== -1;

  if (parsedBudget === null) {
    if (hasBudget && brief.budget > 0) {
      return formatMoney(brief.budget, brief.currency, true);
    }
    return 'Open to offers';
  }

  return parsedBudget || 'Open to offers';
}

/**
 * @param {Object} brief
 * @returns {string}
 * @deprecated TP-604 — setting chip removed; use formatWhereLine.
 */
export function getSettingChipLabel(brief) {
  if (brief.remote) {
    return 'Remote';
  }

  if (!brief.location) {
    return '';
  }

  const parts = brief.location.split(', ');
  if (parts.length > 1) {
    return `${parts[0]}, ${parts[1]}`;
  }

  return `${brief.location} only`;
}

/**
 * Map a collaboration brief (+ viewer) into JobCard view props.
 *
 * @param {Object} brief
 * @param {Object|null} user - current viewer
 * @returns {Object}
 */
export function mapBriefToJobCardProps(brief, user = null) {
  const owner =
    brief.user && typeof brief.user === 'object' ? brief.user : null;
  const displayName = owner?.displayname || '';
  const hideClient = !displayName || HIDDEN_CLIENT_NAME.test(displayName);
  const isPrivate =
    !!displayName &&
    (/user[0-9]+/i.test(displayName) || displayName.toLowerCase() === 'client');
  // Same labels as the job-page ClientCard.
  const clientName = hideClient
    ? ''
    : isPrivate
    ? 'Private client'
    : displayName;

  const { text: title, flags } = parseTitleFlags(brief.text || '');
  const urgent = hasUrgentFlag(flags);
  const searching = hasSearchingFlag(flags);
  const external = Boolean(brief.external);
  const easyApply = !external;
  const isPopular = brief.num_of_pitches > FREE_USER_JOB_PITCH_LIMIT;
  const isPriority = Boolean(brief.boosted_expires_at);
  const userCanApplyToPopular = Boolean(user && (user.pro || user.premium));
  const canApply = !external && (!isPopular || userCanApplyToPopular);

  let status = 'none';
  if (!external) {
    if (isPriority) {
      status = 'priority';
    } else if (isPopular) {
      status = 'popular';
    } else {
      status = 'open';
    }
  }

  const { budget: parsedBudget, details } = parseBriefSpec(brief.spec || '');

  const experience =
    details.find((item) => item.label === 'Experience level')?.value || '';
  const workType =
    details.find((item) => item.label === 'Work type')?.value || '';
  const hiringStage =
    details.find((item) => item.label === 'Hiring stage')?.value || '';
  const readyToHire = /ready to make a paid hire/i.test(hiringStage);

  const roleRaw = brief.role || (brief.roles && brief.roles[0]) || '';
  const activityStatus = external
    ? null
    : getClientActivityStatus(brief, owner);
  const activityLabel = getClientActivityLabel(activityStatus);

  const jobUrl = brief.links?.main_relative || brief.links?.main || '';
  const externalUrl = brief.external_url || '';
  const hostname = getExternalHostname(externalUrl);

  // /jobs always uses "View job" — Ari board cards set their own CTA labels.
  let ctaLabel = 'View job';
  let ctaHref = jobUrl;
  let ctaExternal = false;

  if (external) {
    ctaHref = externalUrl || jobUrl;
    ctaExternal = Boolean(externalUrl);
  }

  const postedAt = brief.approved_at || brief.timestamp;
  const postedLabel = postedAt ? calendarDaysAgoLabel(postedAt) : null;
  const timeLine = postedLabel
    ? external
      ? `Found ${postedLabel}`
      : `Posted ${postedLabel}`
    : '';

  const expiresAt = brief.expires_at;
  const isExpired = expiresAt ? moment().isAfter(moment(expiresAt)) : false;
  let footerMeta = '';
  if (external && postedLabel) {
    footerMeta = `Listed ${postedLabel} on their site`;
  } else if (!external && expiresAt && !isExpired) {
    footerMeta = `Closes in ${moment(expiresAt).fromNow(true)}`;
  }

  return {
    id: brief.id,
    jobUrl,
    source: external ? 'external' : easyApply ? 'easyApply' : 'twine',
    easyApply,
    status,
    urgent,
    searching,
    title,
    client: {
      name: clientName,
      // Full owner so hasAvatar() matches the job-page ClientCard.
      owner: hideClient ? null : owner,
      avatar: hideClient ? null : owner?.avatars || null,
      initials: isPrivate ? '' : getInitials(clientName),
      isPrivate,
      // TP-604: client location removed from the card (stays on the job page).
      location: '',
      lastActiveAt: hideClient ? '' : owner?.last_active || '',
      activityLabel: hideClient ? null : activityLabel,
      show: !hideClient,
    },
    where: formatWhereLine(brief),
    chips: {
      // TP-604: work-setting chip removed; location lives on `where`.
      setting: '',
      budget: getBudgetChipLabel(brief, parsedBudget),
      role: roleRaw ? toRoleSentenceCase(roleRaw) : '',
      experience,
      workType,
      readyToHire,
    },
    excerpt: getJobCardExcerpt(brief.spec || ''),
    closesAt: expiresAt || '',
    listedAt: postedAt || '',
    timeLine,
    footerMeta,
    canApply,
    saved: Boolean(brief.saved_by_current_user),
    pipelineEntryId:
      brief.pipeline_entry_id != null ? brief.pipeline_entry_id : null,
    cta: {
      label: ctaLabel,
      href: ctaHref,
      external: ctaExternal,
    },
    externalHostname: hostname,
    // Reserved for Ari match band + reason (A1).
    ari: null,
  };
}
